import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";

import {
  convertToDefEventPara,
  convertToCurrencyFormat,
  orderStatusOptions,
  yesOrNoOptions,
  isReadOnlyUser,
  formatDate,
  convertStatus,
} from "../../../shared/utility";
import OrderNotesModal from "./OrderNotesModal";
import SelectMenu from "../../controls/SelectMenu";
import OrderProductsTable from "./OrderProductsTable";
import OrderLaboursTable from "./OrderLaboursTable";
import DeleteConfirmationForm from "../DeleteConfirmationForm";
import { useForm } from "../useForm";
import ButtonDropdown from "../../controls/ButtonDropdown";
import TruckInspectionChecklist from "../shared/TruckInspectionChecklist";
import Pills from "../../controls/Pills";

const initialFValues = {
  id: 0,
  customer: null,
  customer_id: null,
  po_number: "",
  order_date: new Date(),
  expected_date: null,
  status: null,
  notes: "",
  items: [],
  credit_returns: [],
  currency: null,
};

function SectionHeading({ children }) {
  return (
    <h3 className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-2 mb-4">
      {children}
    </h3>
  );
}

function ReadOnlyField({ label, value }) {
  return (
    <div>
      <dt className="text-xs font-medium text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium text-gray-900">
        {value || "—"}
      </dd>
    </div>
  );
}

export default function CreateNewOrderForm(props) {
  const {
    carriers,
    customers,
    isEdit,
    loading,
    order,
    setEmptyModelOpen,
    showErrorModal,
    updateOrderApiCall,
    onUpdateOrderItem,
    deleteOrderItemApiCall,
    addProductToOrderApiCall,
    printOrderInvoice,
    printShippingSummary,
    printShippingDetails,
    closed,
    rowDetails,
    fetchRowDetails,
    loadingRows,
    fetchSubRowDetails,
    subRowDetails,
    subLoadingRows,
    selectedItems,
    setSelectedItems,
    currencyOptions,
    manualLaboursAssigned,
    manualLaboursLoading,
    onAddManualLabour,
    onUpdateManualLabour,
    onDeleteManualLabour,
  } = props;

  const isReadOnly = isReadOnlyUser();

  const buttonOptions = [
    {
      name: "Print Invoice",
      onClick: printOrderInvoice,
      inProgress: false,
    },
    {
      name: "Print Shipping Summary",
      onClick: printShippingSummary,
      inProgress: false,
    },
    {
      name: "Print Shipping Slip",
      onClick: printShippingDetails,
      inProgress: false,
    },
  ];

  const validate = (fieldValues = values) => {
    let temp = { ...errors };

    temp.customer = null;
    if (values.customer === "") {
      temp.customer = "Please fill out field.";
    }

    temp.expected_date = null;
    if (values.expected_date === "") {
      temp.expected_date = "Please fill out field.";
    }

    temp.po_number = null;
    if (values.po_number === "") {
      temp.po_number = "Please fill out field.";
    }

    setErrors({
      ...temp,
    });

    if (fieldValues === values) {
      return Object.values(temp).every((x) => x === null);
    }
  };

  const { values, setValues, errors, setErrors, handleInputChange } = useForm(
    initialFValues,
    false,
    validate,
  );

  useEffect(() => {
    if (order) {
      var truckQuestion1 = [];
      if (order.truck_question_1) {
        truckQuestion1 = yesOrNoOptions.find(
          (el) => el.value === order.truck_question_1,
        );
      }

      var truckQuestion2 = [];
      if (order.truck_question_2) {
        truckQuestion2 = yesOrNoOptions.find(
          (el) => el.value === order.truck_question_2,
        );
      }

      var truckQuestion3 = [];
      if (order.truck_question_3) {
        truckQuestion3 = yesOrNoOptions.find(
          (el) => el.value === order.truck_question_3,
        );
      }

      var truckQuestion4 = [];
      if (order.truck_question_4) {
        truckQuestion4 = yesOrNoOptions.find(
          (el) => el.value === order.truck_question_4,
        );
      }

      var orderCurrency = null;
      if (order.currency) {
        orderCurrency = currencyOptions.find(
          (el) => el.value === order.currency,
        );
      }

      if (order.currency === null) {
        orderCurrency = { value: 1, label: "CAD" };
      }

      const orderCopy = {
        ...order,
        status: orderStatusOptions.filter(
          (option) => option.value === order.status,
        )[0],
        customer: customers.filter(
          (option) => option.value === order.customer_id,
        )[0],
        carrier: carriers.filter(
          (option) => option.value === order.carrier_id,
        )[0],
        truck_question_1: truckQuestion1,
        truck_question_2: truckQuestion2,
        truck_question_3: truckQuestion3,
        truck_question_4: truckQuestion4,
        currency: orderCurrency,
      };
      setValues(orderCopy);
    }
  }, [order]);

  const handleSubmit = () => {
    if (validate()) {
      updateOrderApiCall(values);
    }
  };

  const updateItemLocally = (newData, index) => {
    let currentItems = [...values.items];
    currentItems[index] = newData;
    setValues({ ...values, items: currentItems });
  };

  const onItemEditComplete = async function (newData, index) {
    if (isEdit) {
      const onUpdateCompleted = await onUpdateOrderItem(newData);

      if (onUpdateCompleted.success) {
        updateItemLocally(newData, index);
      }
    }
  };

  const makeDeleteOrderItemApiCall = async function (itemIdInDB, product_id) {
    var onItemDeleted = { success: false };
    if (isEdit) {
      onItemDeleted = await deleteOrderItemApiCall(itemIdInDB);
    }

    if (onItemDeleted.success || !isEdit) {
      const filteredItems = values.items.filter(
        (item) => item.product_id !== product_id,
      );
      setValues({ ...values, items: filteredItems });
    }
    setEmptyModelOpen({
      childComponent: null,
      open: false,
    });
  };

  // Not used
  const askDeleteOrderItem = function (product) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() =>
            makeDeleteOrderItemApiCall(product.id, product.product_id)
          }
          message={`Are you sure you want to ${product.product_name}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const labourItems = manualLaboursAssigned?.items ?? [];
  const labourEntryCount = labourItems.length;
  const labourTotal = useMemo(() => {
    if (manualLaboursAssigned?.total_price != null) {
      return parseFloat(manualLaboursAssigned.total_price) || 0;
    }
    return labourItems.reduce(
      (sum, labour) => sum + (parseFloat(labour.total_price) || 0),
      0,
    );
  }, [manualLaboursAssigned]);

  const statusPill =
    isEdit && values.status?.value
      ? convertStatus(values.status.value)
      : null;

  return (
    <div>
      {order || !isEdit ? (
        <div className="grid grid-cols-1 gap-4 h-full xl:grid-cols-3">
          <div className="order-1 xl:col-span-2 space-y-4">
            <div className="card-custom p-4">
              <h2 className="text-base font-semibold text-gray-900 mb-3">
                Line items
              </h2>
              <OrderProductsTable
                loading={false}
                values={values}
                onItemEditComplete={onItemEditComplete}
                rowDetails={rowDetails}
                fetchRowDetails={fetchRowDetails}
                loadingRows={loadingRows}
                fetchSubRowDetails={fetchSubRowDetails}
                subRowDetails={subRowDetails}
                subLoadingRows={subLoadingRows}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
              />
            </div>

            <div className="card-custom p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                <h2 className="text-base font-semibold text-gray-900">
                  Manual labour
                </h2>
                <p className="text-sm text-gray-500">
                  {labourEntryCount} entr
                  {labourEntryCount === 1 ? "y" : "ies"}
                  {labourTotal > 0
                    ? ` · ${convertToCurrencyFormat(labourTotal)}`
                    : ""}
                </p>
              </div>
              <OrderLaboursTable
                loading={manualLaboursLoading}
                manualLaboursAssigned={manualLaboursAssigned}
                onAddLabour={onAddManualLabour}
                onUpdateLabour={onUpdateManualLabour}
                onDeleteLabour={onDeleteManualLabour}
                closed={closed}
              />
            </div>
          </div>

          <div className="order-2 xl:col-span-1 xl:sticky xl:top-4 xl:self-start space-y-4">
            {!isReadOnly && isEdit ? (
              <div className="card-custom p-3">
                <div className="relative z-50 flex w-full overflow-visible">
                  <ButtonDropdown
                    mainOption="Save"
                    items={buttonOptions}
                    mainAction={handleSubmit}
                  />
                </div>
              </div>
            ) : null}

            <div className="card-custom p-4">
              <div className="flex items-center justify-between gap-2 border-b border-gray-200 pb-2 mb-4">
                <h3 className="text-sm font-semibold text-gray-900">
                  Order summary
                </h3>
                {statusPill ? <Pills content={statusPill} /> : null}
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <ReadOnlyField
                  label="PO #"
                  value={values.po_number}
                />
                <ReadOnlyField
                  label="Shipped date"
                  value={formatDate(values.created_at)}
                />
                <ReadOnlyField
                  label="Created by"
                  value={values.created_by_name}
                />
              </dl>
            </div>

            <div className="card-custom p-4">
              <SectionHeading>Order details</SectionHeading>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <SelectMenu
                  handleInputChange={handleInputChange}
                  name="customer"
                  options={customers.filter((option) => option.value !== -1)}
                  value={isEdit ? values.customer : null}
                  title="Customer"
                  error={errors.customer}
                  showLabel={true}
                  disabled={closed}
                  parentClass="sm:col-span-2"
                />

                <SelectMenu
                  handleInputChange={handleInputChange}
                  name="currency"
                  options={currencyOptions.filter(
                    (option) => option.value !== -1,
                  )}
                  value={isEdit ? values.currency : null}
                  title="Currency"
                  error={errors.currency}
                  showLabel={true}
                  disabled={closed}
                />

                {isEdit ? (
                  <SelectMenu
                    handleInputChange={handleInputChange}
                    name="status"
                    options={orderStatusOptions.filter(
                      (option) => option.value !== -1,
                    )}
                    value={isEdit ? values.status : null}
                    title="Status"
                    error={errors.status}
                    showLabel={true}
                  />
                ) : null}
              </div>
            </div>

            <div className="card-custom p-4">
              <SectionHeading>Shipping</SectionHeading>
              <SelectMenu
                handleInputChange={handleInputChange}
                name="carrier"
                options={carriers.filter((option) => option.value !== -1)}
                value={isEdit ? values.carrier : null}
                title="Carrier"
                error={errors.carrier}
                showLabel={true}
                disabled={closed}
              />
            </div>

            <TruckInspectionChecklist
              values={values}
              errors={errors}
              handleInputChange={handleInputChange}
              disabled={closed}
            />

            {values.credit_returns?.length > 0 ? (
              <div className="card-custom p-4">
                <SectionHeading>Credit returns</SectionHeading>
                <div className="space-y-2">
                  {values.credit_returns.map((cr) => (
                    <Link
                      key={cr.id}
                      to={`/credit-returns/${cr.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="block rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white text-center shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                      {cr.name || `CR #${cr.id}`}
                    </Link>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="card-custom p-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-sm font-semibold text-gray-900">Notes</h3>
                <button
                  onClick={() =>
                    setEmptyModelOpen({
                      childComponent: (
                        <OrderNotesModal
                          orderNote={values.notes}
                          confirmAction={(newNote) =>
                            handleInputChange(
                              convertToDefEventPara("notes", newNote),
                            )
                          }
                          setOpen={setEmptyModelOpen}
                        />
                      ),
                      open: true,
                    })
                  }
                  className="text-sm font-semibold main-purple"
                  disabled={closed}
                >
                  Edit
                </button>
              </div>
              <p className="text-sm text-gray-600 whitespace-pre-wrap line-clamp-4">
                {values.notes ? values.notes : "No notes"}
              </p>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
